import React, { useEffect, useRef, useState } from 'react';
import { FaEye, FaEyeSlash, FaLock, FaEnvelope } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import '../index.css';
import logo from "../images/logo.png";
import dashboardImage from "../images/dashboard-invX.jpeg";

const API_BASE = '/api';

const Login = ({ onLogin }) => {
  // Auth form
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState(''); // used both for login and new password
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [infoMsg, setInfoMsg] = useState('');
  const [loading, setLoading] = useState(false);

  // animation / reset flow
  const [showPasswordFields, setShowPasswordFields] = useState(false); // whether reset fields are visible
  const [swapped, setSwapped] = useState(false); // controls transform of panels
  const [animating, setAnimating] = useState(false); // used to disable clicks while animating

  const [showPassword, setShowPassword] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(true);
  const navigate = useNavigate();

  // Reset-flow combined UI
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState('');
  const [showOtpAndPassword, setShowOtpAndPassword] = useState(false); // after sending OTP, show OTP + new password
  const [resendCountdown, setResendCountdown] = useState(0);
  const resendTimerRef = useRef(null);

  // Password policy:
  // - first letter must be capital
  // - then alphabets
  // - then at least one special char
  // - then numbers
  // - total length > 8 (so >=9)
  const passwordRegex = /^(?=.{9,}$)[A-Z][a-zA-Z]+[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+[0-9]+$/;

  // ---------- helper: hashing & storage for previous passwords ----------
  const hashPassword = async (pwd) => {
    const enc = new TextEncoder();
    const data = enc.encode(pwd);
    const hashBuffer = await window.crypto.subtle.digest('SHA-256', data);
    // convert to hex
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
    return hashHex;
  };

  const storageKeyForEmail = (mail) => {
    if (!mail) return null;
    return `pw_hashes_${mail.toLowerCase()}`;
  };

  const getStoredHashes = (mail) => {
    try {
      const key = storageKeyForEmail(mail);
      if (!key) return [];
      const raw = localStorage.getItem(key);
      if (!raw) return [];
      return JSON.parse(raw);
    } catch (err) {
      console.warn('Failed to read stored password hashes', err);
      return [];
    }
  };

  const saveHashForEmail = (mail, hash) => {
    try {
      const key = storageKeyForEmail(mail);
      if (!key) return;
      const prev = getStoredHashes(mail);
      // avoid duplicates
      if (!prev.includes(hash)) {
        prev.push(hash);
        localStorage.setItem(key, JSON.stringify(prev));
      }
    } catch (err) {
      console.warn('Failed to save password hash', err);
    }
  };

  // ---------- LOGIN ----------
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await fetch(`${API_BASE}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' ,'authorization': `Bearer ${localStorage.getItem('authToken')}`},
        body: JSON.stringify({ email, password }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.message || 'Login failed');
      } else {
        if (data.token) {
          localStorage.setItem('authToken', data.token);
        }

        try {
          const token = localStorage.getItem('authToken');
          const userRes = await fetch(`${API_BASE}/current-user`, {
            headers: { 'Authorization': `Bearer ${token}` },
          });

          const userData = await userRes.json();

          if (userRes.ok && userData.user) {
            onLogin && onLogin(userData.user);
            navigate('/dashboard', { state: { user: userData.user } });
          } else {
            setError(userData.message || 'Failed to fetch user details after login.');
          }
        } catch (fetchError) {
          console.error('Failed to fetch current user:', fetchError);
          setError('Error fetching user details.');
        }
      }
    } catch (err) {
      console.error('Login network error:', err);
      setError('Network error');
    } finally {
      setLoading(false);
    }
  };

  // ---------- SWAP ANIMATION ----------
  const handleStartReset = () => {
    if (animating) return;
    setAnimating(true);
    setSwapped(true);

    setTimeout(() => {
      setShowPasswordFields(true);
      setAnimating(false);
      // initialize reset state
      setShowOtpAndPassword(false);
      setOtp('');
      setPassword('');
      setConfirmPassword('');
      setOtpError('');
      setInfoMsg('');
      clearResendTimer();
    }, 600);
  };

  const handleCancelReset = () => {
    if (animating) return;
    setAnimating(true);
    setShowPasswordFields(false);
    setSwapped(false);

    setTimeout(() => {
      setAnimating(false);
      // cleanup reset state
      setShowOtpAndPassword(false);
      setOtp('');
      setPassword('');
      setConfirmPassword('');
      setOtpError('');
      setInfoMsg('');
      clearResendTimer();
    }, 600);
  };

  // ---------- RESEND TIMER ----------
  const startResendCountdown = (sec = 60) => {
    setResendCountdown(sec);
    clearResendTimer();
    resendTimerRef.current = setInterval(() => {
      setResendCountdown((s) => {
        if (s <= 1) {
          clearResendTimer();
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  };

  const clearResendTimer = () => {
    if (resendTimerRef.current) {
      clearInterval(resendTimerRef.current);
      resendTimerRef.current = null;
    }
    setResendCountdown(0);
  };

  useEffect(() => {
    return () => clearResendTimer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------- SEND OTP (Step shown first) ----------
  const handleSendOTP = async (e) => {
    e && e.preventDefault();
    setError('');
    setInfoMsg('');
    setOtpError('');

    if (!email) {
      setError('Please enter your email first.');
      return;
    }

    try {
      setLoading(true);
      const res = await fetch(`${API_BASE}/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      const data = await res.json();
      if (!res.ok) {
        setError(data.message || 'Failed to send OTP.');
        return;
      }

      // success
      setInfoMsg(data.message || 'OTP sent to your email. It is valid for 5 minutes.');
      setShowOtpAndPassword(true); // now show OTP + password together
      setOtp('');
      startResendCountdown(60);
    } catch (err) {
      console.error('Send OTP error', err);
      // show server message if any, otherwise network error
      setError(err.message || 'Network error while sending OTP.');
    } finally {
      setLoading(false);
    }
  };

  // ---------- RESEND OTP ----------
  const handleResendOTP = async () => {
    if (resendCountdown > 0) return;
    setError('');
    setInfoMsg('');
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE}/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      const data = await res.json();
      if (!res.ok) {
        setError(data.message || 'Failed to resend OTP.');
        return;
      }

      setInfoMsg(data.message || 'OTP resent to your email.');
      startResendCountdown(60);
    } catch (err) {
      console.error('Resend OTP error', err);
      setError(err.message || 'Network error while resending OTP.');
    } finally {
      setLoading(false);
    }
  };

  // ---------- CONFIRM OTP + RESET PASSWORD (single action) ----------
  // Backend expects { email, otp, newPassword, confirmPassword } in one call
  const handleConfirmAndReset = async (e) => {
    e && e.preventDefault();
    setError('');
    setOtpError('');
    setInfoMsg('');

    // basic validation
    if (!otp || otp.trim().length === 0) {
      setOtpError('Please enter the OTP sent to your email.');
      return;
    }
    if (!password) {
      setError('Please enter a new password.');
      return;
    }
    if (!confirmPassword) {
      setError('Please confirm your new password.');
      return;
    }
    if (password !== confirmPassword) {
      setError('New password and confirm password do not match.');
      return;
    }
    if (!passwordRegex.test(password)) {
      setError(
        'Password rule: First letter uppercase, then letters, at least one special character, then numbers, and total characters greater than 8.'
      );
      return;
    }

    // ---------- new check: prevent reuse of previous passwords ----------
    try {
      if (!email) {
        // should not happen because OTP was sent for an email, but guard anyway
        setError('Email unknown — please start the reset flow again.');
        return;
      }
      const newHash = await hashPassword(password);
      const prevHashes = getStoredHashes(email);
      if (prevHashes.includes(newHash)) {
        setError('You cannot reuse a previous password. Please choose a fresh new password.');
        return;
      }
    } catch (hashErr) {
      console.error('Hash check failed', hashErr);
      // do not block user if hashing fails — but warn
      setError('Unexpected error while validating password uniqueness. Try again.');
      return;
    }

    try {
      setLoading(true);

      // Single call to /reset-password with all required fields
      const resetRes = await fetch(`${API_BASE}/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          otp,
          newPassword: password,
          confirmPassword,
        }),
      });

      const resetData = await resetRes.json();

      if (!resetRes.ok) {
        // show server error (invalid otp / mismatch / expired)
        // use otp-specific message if provided
        const msg = resetData.message || 'Failed to reset password.';
        // if server indicates OTP issue, set otpError
        if (msg.toLowerCase().includes('otp') || resetRes.status === 400) {
          setOtpError(msg);
        } else {
          setError(msg);
        }
        return;
      }

      // On success, save hashed password to localStorage for future reuse checks.
      try {
        const newHash = await hashPassword(password);
        saveHashForEmail(email, newHash);
      } catch (saveErr) {
        console.warn('Failed to persist new password hash', saveErr);
        // not fatal: continue
      }

      setInfoMsg(resetData.message || 'Password reset successful. You can now log in with your new password.');
      // small delay to show success then swap back
      setTimeout(() => {
        handleCancelReset();
      }, 900);
    } catch (err) {
      console.error('Confirm & Reset error', err);
      // show server-supplied message if available on the error object, otherwise generic
      setError(err.message || 'Network error during reset.');
    } finally {
      setLoading(false);
    }
  };

  // ---------- RENDER ----------
  return (
    <div className="w-full h-screen bg-slate-100 flex items-center justify-center">
      <div className="w-full h-full bg-white shadow-xl overflow-hidden flex flex-col md:flex-row relative">
        <div
          className={`flex w-full h-full transition-transform duration-600 ease-in-out relative md:flex-row flex-col ${
            swapped ? 'panels-swapped' : ''
          }`}
          style={{ height: '100%' }}
        >
          {/* LEFT: form panel */}
          <div
            className={`panel-left w-full md:w-1/2 p-8 md:p-16 overflow-auto flex items-start transition-transform duration-600 ease-in-out`}
            aria-hidden={animating}
          >
            <div className="w-full max-w-md mx-auto">
              {/* Logo */}
              <div className="flex items-center mb-8">
                <img src={logo} alt="logo" className="w-12 h-12 object-contain mr-3" />
                <div>
                  <h2 className="text-2xl font-bold" style={{ color: '#0f4b4f', fontFamily: 'sans-serif' }}>
                    <span style={{ fontFamily: "'Poppins', sans-serif", letterSpacing: '1px', fontWeight: 700 }}>
                      Portal
                    </span>
                    <span
                      style={{
                        fontFamily: "'Pacifico', cursive",
                        color: '#0b4a50',
                        marginLeft: '2px',
                      }}
                    >
                      X
                    </span>
                    <span
                      style={{
                        fontFamily: "'Inter', sans-serif",
                        fontWeight: 500,
                        marginLeft: '6px',
                        color: '#0f4b4f',
                      }}
                    >
                      Inventory Management
                    </span>
                  </h2>
                </div>
              </div>

              {!showPasswordFields ? (
                <>
                  {/* Login */}
                  <h1 className="text-3xl font-bold text-slate-900 mb-4">Login </h1>
                  <p className="text-sm text-slate-500 mb-6">Generate inventory summary reports with date range and location filters.</p>

                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-2">Email</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                          <FaEnvelope />
                        </span>
                        <input
                          type="email"
                          placeholder="Enter your mail"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          required
                          disabled={loading}
                          className="w-full pl-10 pr-4 py-3 rounded-md bg-slate-50 border border-slate-100 text-slate-800 placeholder-slate-400 focus:outline-none"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-2">Password</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                          <FaLock />
                        </span>
                        <input
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Enter your password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                          disabled={loading}
                          onCopy={(e) => {
                            // prevent copying password to clipboard
                            e.preventDefault();
                            setInfoMsg('Copying the password is disabled for security.');
                            // clear this message after short delay
                            setTimeout(() => setInfoMsg(''), 2500);
                          }}
                          className="w-full pl-10 pr-12 py-3 rounded-md bg-slate-50 border border-slate-100 text-slate-800 placeholder-slate-400 focus:outline-none hide-password-reveal"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                        >
                          {showPassword ? <FaEyeSlash /> : <FaEye />}
                        </button>
                      </div>
                    </div>

                    {error && <div className="text-sm text-red-700 bg-red-50 px-3 py-2 rounded-md">{error}</div>}
                    {infoMsg && <div className="text-sm text-green-800 bg-green-50 px-3 py-2 rounded-md">{infoMsg}</div>}

                    <label className="flex items-center gap-3 text-sm text-slate-600">
                      <input
                        type="checkbox"
                        checked={agreeTerms}
                        onChange={() => setAgreeTerms((v) => !v)}
                        className="w-4 h-4 rounded border-slate-300"
                      />
                      <span>
                        I agree to all the <a className="underline text-slate-800">Terms & Conditions</a>
                      </span>
                    </label>

                    <button
                      type="submit"
                      disabled={loading || !agreeTerms}
                      className={`w-full py-3 rounded-md text-white font-medium shadow-sm transition ${
                        loading || !agreeTerms ? 'opacity-60 cursor-not-allowed' : ''
                      }`}
                      style={{
                        background: 'linear-gradient(180deg,#083b40,#0f6b70)',
                      }}
                    >
                      {loading ? 'Signing in...' : 'Sign up'}
                    </button>

                    <div className="mt-2 text-center">
                      <p className="text-sm text-slate-600">
                        Forgot your password?{' '}
                        <button
                          type="button"
                          onClick={handleStartReset}
                          className="text-sm underline"
                          style={{ color: '#0f6b70' }}
                          disabled={animating}
                        >
                          Reset it?
                        </button>
                      </p>
                    </div>
                  </form>
                </>
              ) : (
                <>
                  {/* Reset: combined OTP + New password on same page */}
                  <h1 className="text-3xl font-bold text-slate-900 mb-4">Reset password</h1>
                  <p className="text-sm text-slate-500 mb-6">Enter email → send OTP → then enter OTP and new password on the same screen.</p>

                  {/* If OTP not yet sent, show Send OTP form (only email) */}
                  {!showOtpAndPassword ? (
                    <form onSubmit={handleSendOTP} className="space-y-4">
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-2">Email</label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                            <FaEnvelope />
                          </span>
                          <input
                            type="email"
                            placeholder="Enter your mail"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            disabled={loading}
                            className="w-full pl-10 pr-4 py-3 rounded-md bg-slate-50 border border-slate-100 text-slate-800 placeholder-slate-400 focus:outline-none"
                          />
                        </div>
                      </div>

                      {error && <div className="text-sm text-red-700 bg-red-50 px-3 py-2 rounded-md">{error}</div>}
                      {infoMsg && <div className="text-sm text-green-800 bg-green-50 px-3 py-2 rounded-md">{infoMsg}</div>}

                      <div className="flex gap-3">
                        <button
                          type="submit"
                          disabled={loading}
                          className={`flex-1 py-3 rounded-md text-white font-medium shadow-sm ${
                            loading ? 'opacity-60 cursor-not-allowed' : ''
                          }`}
                          style={{
                            background: 'linear-gradient(180deg,#0b7b77,#0b6b62)',
                          }}
                        >
                          {loading ? 'Sending OTP...' : 'Send OTP'}
                        </button>
                        <button
                          type="button"
                          onClick={handleCancelReset}
                          className="flex-1 py-3 rounded-md border border-slate-200 text-slate-700"
                          disabled={animating}
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  ) : (
                    // OTP + Password together
                    <form onSubmit={handleConfirmAndReset} className="space-y-4">
                      {/* show email here (readonly) */}
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-2">Resetting for</label>
                        <input
                          type="email"
                          value={email}
                          readOnly
                          className="w-full pl-10 pr-4 py-3 rounded-md bg-slate-100 border border-slate-100 text-slate-700 placeholder-slate-400 focus:outline-none"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-2">OTP</label>
                        <input
                          type="text"
                          placeholder="Enter 6-digit OTP"
                          value={otp}
                          onChange={(e) => setOtp(e.target.value)}
                          required
                          disabled={loading}
                          className="w-full pl-4 pr-4 py-3 rounded-md bg-slate-50 border border-slate-100 text-slate-800 placeholder-slate-400 focus:outline-none"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-2">New Password</label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                            <FaLock />
                          </span>
                          <input
                            type={showPassword ? 'text' : 'password'}
                            placeholder="Enter new password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            disabled={loading}
                            onCopy={(e) => {
                              // prevent copying new password to clipboard
                              e.preventDefault();
                              setInfoMsg('Copying the password is disabled for security.');
                              setTimeout(() => setInfoMsg(''), 2500);
                            }}
                            className="w-full pl-10 pr-12 py-3 rounded-md bg-slate-50 border border-slate-100 text-slate-800 placeholder-slate-400 focus:outline-none hide-password-reveal"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                          >
                            {showPassword ? <FaEyeSlash /> : <FaEye />}
                          </button>
                        </div>

                        <p className="text-xs text-slate-500 mt-2">
                          Rule: First letter uppercase, then letters, at least one special character, then numbers. Total length &gt; 8.
                        </p>
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-2">Confirm Password</label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                            <FaLock />
                          </span>
                          <input
                            type={showPassword ? 'text' : 'password'}
                            placeholder="Confirm new password"
                            value={confirmPassword}
                            onChange={(e) => {
                              setConfirmPassword(e.target.value);
                              // clear any paste-specific error message on manual typing
                              if (error && error.toLowerCase().includes('paste')) {
                                setError('');
                              }
                            }}
                            required
                            disabled={loading}
                            onPaste={(e) => {
                              e.preventDefault();
                              setError('Pasting into Confirm Password is disabled. Please type it manually.');
                            }}
                            onDrop={(e) => {
                              e.preventDefault();
                              setError('Dropping content into Confirm Password is disabled. Please type it manually.');
                            }}
                            className={`w-full pl-10 pr-12 py-3 rounded-md ${
                              confirmPassword && password === confirmPassword ? 'border-green-400' : 'border-slate-100'
                            } bg-slate-50 text-slate-800 placeholder-slate-400 focus:outline-none hide-password-reveal`}
                          />
                        </div>
                        {confirmPassword && password !== confirmPassword && (
                          <p className="text-xs text-red-600 mt-1">Passwords do not match.</p>
                        )}
                        {confirmPassword && password && password === confirmPassword && (
                          <p className="text-xs text-green-700 mt-1">Passwords match.</p>
                        )}
                      </div>

                      {(otpError || error) && (
                        <div className="text-sm text-red-700 bg-red-50 px-3 py-2 rounded-md">{otpError || error}</div>
                      )}
                      {infoMsg && <div className="text-sm text-green-800 bg-green-50 px-3 py-2 rounded-md">{infoMsg}</div>}

                      <div className="flex items-center gap-3">
                        <button
                          type="submit"
                          disabled={loading}
                          className={`flex-1 py-3 rounded-md text-white font-medium shadow-sm ${
                            loading ? 'opacity-60 cursor-not-allowed' : ''
                          }`}
                          style={{
                            background: 'linear-gradient(180deg,#0b7b77,#0b6b62)',
                          }}
                        >
                          {loading ? 'Processing...' : 'Confirm OTP & Reset'}
                        </button>

                        <button
                          type="button"
                          onClick={handleResendOTP}
                          disabled={resendCountdown > 0 || loading}
                          className={`py-3 px-4 rounded-md border border-slate-200 text-slate-700 ${
                            resendCountdown > 0 ? 'opacity-60 cursor-not-allowed' : ''
                          }`}
                        >
                          {resendCountdown > 0 ? `Resend in ${resendCountdown}s` : 'Resend OTP'}
                        </button>
                      </div>

                      <div className="flex gap-3">
                        <button
                          type="button"
                          onClick={() => {
                            // go back to only-send-otp view so user can re-enter email if needed
                            setShowOtpAndPassword(false);
                            setOtp('');
                            setPassword('');
                            setConfirmPassword('');
                            setOtpError('');
                            setInfoMsg('');
                            clearResendTimer();
                          }}
                          className="flex-1 py-3 rounded-md border border-slate-200 text-slate-700"
                        >
                          Back
                        </button>

                        <button
                          type="button"
                          onClick={handleCancelReset}
                          className="flex-1 py-3 rounded-md border border-slate-200 text-slate-700"
                          disabled={animating}
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  )}
                </>
              )}
            </div>
          </div>

          {/* RIGHT: teal hero panel with text on top and resized image */}
          <div
            className={`panel-right w-full md:w-1/2 flex flex-col justify-start items-center p-8 relative transition-transform duration-600 ease-in-out`}
            style={{ backgroundColor: '#0b4a50' }}
            aria-hidden={animating}
          >
            <div className="text-center text-white mt-6 mb-6">
              <h3
                className="text-2xl md:text-3xl font-semibold flex justify-center items-center text-center flex-row space-x-4 mb-2"
                style={{ fontFamily: 'initial' }}
              >
                <span>Smarter Inventory</span> <span>Better Decisions </span>
              </h3>
              <p className="text-sm md:text-base text-slate-200">
                Gain real-time control over your stock and streamline daily operations effortlessly.
              </p>
            </div>

            <div className="flex items-center justify-center w-full mt-4">
              <img
                src={dashboardImage}
                alt="dashboard preview"
                className="max-w-full w-[260px] md:w-[420px] lg:w-[500px] h-auto rounded-2xl object-cover shadow-2xl border-4 border-white"
                style={{
                  transform: 'rotate(0deg)',
                  transition: 'transform 300ms ease',
                }}
              />
            </div>

            <div style={{ position: 'absolute', right: 24, bottom: 24, opacity: 0.08 }}>
              <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(4,18px)' }}>
                <div className="w-4 h-4 bg-white rounded-sm" />
                <div className="w-4 h-4 bg-white rounded-sm opacity-70" />
                <div className="w-4 h-4 bg-white rounded-sm opacity-50" />
                <div className="w-4 h-4 bg-white rounded-sm opacity-30" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Small CSS tweaks + animation rules */}
      <style jsx>{`
        :root {
          --teal-dark: #0b4a50;
          --teal-light: #dbeff0;
        }
        /* hide native browser password reveal button */
        .hide-password-reveal::-ms-reveal,
        .hide-password-reveal::-webkit-credentials-auto-fill-button {
          display: none;
        }

        .transition-transform {
          transition-property: transform;
        }
        .duration-600 {
          transition-duration: 600ms;
        }

        .panel-left {
          z-index: 1;
          transform: translateX(0%);
        }
        .panel-right {
          z-index: 2;
          transform: translateX(0%);
        }

        .panels-swapped .panel-left {
          transform: translateX(100%);
          z-index: 1;
        }
        .panels-swapped .panel-right {
          transform: translateX(-100%);
          z-index: 2;
        }

        .panel-left,
        .panel-right {
          backface-visibility: hidden;
          will-change: transform;
        }

        @media (max-width: 768px) {
          .panels-swapped .panel-left {
            transform: translateY(-6px) translateX(0%);
          }
          .panels-swapped .panel-right {
            transform: translateY(6px) translateX(0%);
          }
        }
      `}</style>
    </div>
  );
};

export default Login;
